import React, { useState } from 'react';
import s from './s.module.less';
import ProviderStore from 'store/Provider';
import { Provider } from 'types/provider';
import useChannelDetail from 'hooks/useChannelDetail';
import { getPPAChannelId } from 'utils/common';
import { Input, Button, Select, message } from 'antd';
import { STRIPE_PUBLIC_KEY } from 'constants/stripe';
import { loadStripe, PaymentRequest } from '@stripe/stripe-js';
import countries from 'constants/country';
import { useElements, useStripe, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { emailRegex } from 'pages/SalesProviderExp/constant';
import useLoadBasicData from 'hooks/useLoadBasicData';
import { channelPay, getSecretForBankPay, submitRecommandChannels, updateChannelPay } from 'api/channel';
import { PATH } from 'constants/path';
import { useNavigate, useSearchParams } from 'react-router-dom';

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const filedName = {
    cardNumber: 'cardNumber',
    cardExpiry: 'cardExpiry',
    cardCvc: 'cardCvc',
    email: 'email',
    country: 'country',
    zipCode: 'zipCode',
};

export default function ChannelPaymentPage() {
    const [loadBasicData] = useLoadBasicData();
    const [channel] = useChannelDetail(Number(getPPAChannelId()), true);
    const [getUser] = ProviderStore.useStore();
    const user: Provider = getUser('data');
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const type = searchParams.get('type');

    const [email, setEmail] = useState('');
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');

    const [country, setCountry] = useState('US');
    const [zipCode, setZipCode] = useState('');
    const [showErrors, setShowErrors] = useState(false);

    const [loading, setLoading] = useState(false);

    const [canShowGooglePay, setCanShowGooglePay] = useState(false);
    const [googlePaymentRequest, setGooglePaymentRequest] = useState<any>(null);

    const [androidPaymentRequest, setAndroidPaymentRequest] = useState<PaymentRequest | null>(null);
    const [androidPaymentError, setAndroidPaymentError] = useState<string | null>(null);
    const [androidPaymentSucceeded, setAndroidPaymentSucceeded] = useState<boolean>(false);

    const bankUsePaymentIntant = true;

    // 银行账户信息状态
    const [bankAccountInfo, setBankAccountInfo] = useState({
        accountHolderName: '',
        routingNumber: '',
        accountNumber: '',
        accountType: 'checking',
        country: 'US',
        zipCode: '',
    });

    const [fieldsVerifyResult, setFieldsVerifyResult] = useState({
        [filedName.cardNumber]: {
            error: true,
            message: 'Card number is required',
        },
        [filedName.cardExpiry]: {
            error: true,
            message: 'Card expiry date is required',
        },
        [filedName.cardCvc]: {
            error: true,
            message: 'Card CVC is required',
        },
        [filedName.email]: {
            error: false,
            message: 'Please enter the correct email',
        },
        [filedName.zipCode]: {
            error: true,
            message: 'This field is required',
        },
        [filedName.country]: {
            error: false,
            message: 'This field is required',
        },
    });

    React.useEffect(() => {
        if (user) {
            setEmail(user.email || '');
        }
    }, [user]);

    React.useEffect(() => {
        loadBasicData();
    }, [loadBasicData]);

    // React.useEffect(() => {
    //     if (stripe) {
    //         const pr = stripe.paymentRequest({
    //             country: 'US',
    //             currency: 'usd',
    //             total: {
    //                 label: '测试商品',
    //                 amount: 100, // 金额为分
    //             },
    //             requestPayerName: true,
    //             requestPayerEmail: true,
    //         });

    //         pr.canMakePayment().then((result) => {
    //             console.log({
    //                 result,
    //             });
    //             if (result) {
    //                 setAndroidPaymentRequest(pr);
    //             } else {
    //                 setAndroidPaymentRequest(null);
    //             }
    //         });

    //         setGooglePaymentRequest(pr);
    //     }
    // }, [stripe]);

    const handleInputChange = (e: any, t: string) => {
        if (t === filedName.cardCvc || t === filedName.cardExpiry || t === filedName.cardNumber) {
            // e.complete && e.error (e.error.message)
            const correct = e.complete && !e.error;
            setFieldsVerifyResult({
                ...fieldsVerifyResult,
                [t]: {
                    error: !correct,
                    message: correct ? '' : e.error?.message,
                },
            });
        } else if (t === filedName.country) {
            setCountry(e);
            setFieldsVerifyResult({
                ...fieldsVerifyResult,
                [t]: {
                    error: !e,
                    message: e ? '' : 'This field is required',
                },
            });
        } else if (t === filedName.email) {
            setEmail(e);
            const emailVerify = emailRegex.test(e);
            setFieldsVerifyResult({
                ...fieldsVerifyResult,
                [t]: {
                    error: !emailVerify,
                    message: emailVerify ? '' : 'Please enter the correct email',
                },
            });
        } else {
            if (t === filedName.zipCode) {
                setZipCode(e);
            }
            setFieldsVerifyResult({
                ...fieldsVerifyResult,
                [t]: {
                    error: !e,
                    message: e ? '' : 'This field is required',
                },
            });
        }
    };

    // 银行账户输入处理函数
    const handleBankInputChange = (field: string, value: string) => {
        setBankAccountInfo((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // 路由号验证（美国银行路由号是9位数字）
    const validateRoutingNumber = (routingNumber: string) => {
        return /^\d{9}$/.test(routingNumber);
    };

    // 账户号验证（通常4-17位数字）
    const validateAccountNumber = (accountNumber: string) => {
        return /^\d{4,17}$/.test(accountNumber);
    };

    const handleConfirm = async () => {
        // if (!email || !selectedPaymentMethod) {
        //     alert('Please fill in all required fields');
        //     return;
        // }

        setShowErrors(true);

        if (!elements || !stripe) {
            return;
        }

        if (selectedPaymentMethod === 'card') {
            if (Object.values(fieldsVerifyResult).every((e) => e.error)) {
                return;
            }
            const cardElement = elements.getElement(CardNumberElement);
            setLoading(true);
            const res = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement!,
            });
            if (!res.error) {
                const paymentMethodId = res.paymentMethod?.id;
                if (type !== 'update') {
                    const res2 = await channelPay({
                        channelId: channel?.id!,
                        paymentMethodId,
                        email,
                        country,
                        postalCode: zipCode,
                    });
                    if (!res2.error) {
                        const res3 = await submitRecommandChannels({
                            channelIdList: [channel?.id!],
                            multServiceList: [],
                        });
                        navigate(`${PATH.CHANNEL_PAYMENT_SUCCESS}`);
                    }
                } else {
                    const res2 = await updateChannelPay({
                        paymentMethodId,
                    });
                    if (!res2.error) {
                        message.success('Payment method updated successfully. Please note that it may take some time for the changes to take effect.');
                        navigate(-1);
                    }
                }
            }
            setLoading(false);
        } else if (selectedPaymentMethod === 'bank') {
            try {
                if (!bankAccountInfo.accountHolderName || !bankAccountInfo.country || !bankAccountInfo.zipCode) {
                    return;
                }

                //get clientSecret from backend
                setLoading(true);
                const resSecret = await getSecretForBankPay({
                    email,
                    country: bankAccountInfo.country,
                    postalCode: bankAccountInfo.zipCode,
                });
                if (!resSecret.error) {
                    const clientSecret = resSecret.data?.data;
                    const res = await stripe.collectBankAccountForSetup({
                        clientSecret,
                        params: {
                            payment_method_type: 'us_bank_account',
                            payment_method_data: {
                                billing_details: {
                                    name: bankAccountInfo.accountHolderName,
                                    email,
                                },
                            },
                        },
                    });
                    console.log({
                        res,
                    });
                    if (res && res.setupIntent && res.setupIntent.payment_method) {
                        const paymentMethodId = res.setupIntent.payment_method as string;
                        if (res.setupIntent.status === 'requires_confirmation') {
                            const confirmRes = await stripe.confirmUsBankAccountSetup(clientSecret, {
                                payment_method: paymentMethodId,
                                mandate_data: {
                                    customer_acceptance: {
                                        type: 'online',
                                        online: { infer_from_client: true },
                                    },
                                },
                            } as any);
                            console.log({
                                confirmRes,
                            });
                            if (confirmRes.error) {
                                message.error(confirmRes.error.message || 'Bank account confirmation failed');
                                return;
                            }
                            // 重新取确认结果
                            if (
                                !confirmRes.setupIntent ||
                                (confirmRes.setupIntent.status !== 'succeeded' &&
                                    confirmRes.setupIntent.status !== 'processing')
                            ) {
                                message.error('Bank account confirmation failed');
                                return;
                            }
                        } else if (res.setupIntent.status !== 'succeeded' && res.setupIntent.status !== 'processing') {
                            message.error('Mandate authorization failed');
                            return;
                        }
                        if (type !== 'update') {
                            const res2 = await channelPay({
                                channelId: channel?.id!,
                                paymentMethodId: paymentMethodId!,
                                email,
                                country,
                                postalCode: zipCode,
                            });
                            if (!res2.error) {
                                const res3 = await submitRecommandChannels({
                                    channelIdList: [channel?.id!],
                                    multServiceList: [],
                                });
                                navigate(`${PATH.CHANNEL_PAYMENT_SUCCESS}`);
                            }
                        } else {
                            const res2 = await updateChannelPay({
                                paymentMethodId: paymentMethodId!,
                            });
                            if (!res2.error) {
                                message.success('Payment method updated successfully. Please note that it may take some time for the changes to take effect.');
                                navigate(-1);
                            }
                        }
                    }
                }
                // const res = await stripe.collectBankAccountForPayment(
                //     {
                //         clientSecret: 'pi_3RsKtVLWCWKPYTPj1UtXXVDL_secret_u5IWu2xRbwVvvHwOWZ9N9enUE',
                //         params: {
                //             payment_method_type: 'us_bank_account',
                //             payment_method_data: {
                //                 billing_details: {
                //                     name: bankAccountInfo.accountHolderName,
                //                     email,
                //                 },
                //             },
                //         },
                //     });
            } catch (e) {
                console.error('Error creating bank PaymentMethod:', e);
            } finally {
                setLoading(false);
            }

            // try {
            //     // 验证银行账户信息完整性
            //     if (!bankAccountInfo.accountHolderName ||
            //         !bankAccountInfo.routingNumber ||
            //         !bankAccountInfo.accountNumber ||
            //         !bankAccountInfo.accountType ||
            //         !bankAccountInfo.country ||
            //         !bankAccountInfo.zipCode
            //     ) {
            //         return;
            //     }
            //     setLoading(true);
            //     // 创建银行账户 PaymentMethod
            //     const result = await stripe.createPaymentMethod({
            //         type: 'us_bank_account',
            //         us_bank_account: {
            //             routing_number: bankAccountInfo.routingNumber,
            //             account_number: bankAccountInfo.accountNumber,
            //             account_holder_type: 'individual', // 或 'company'
            //             account_type: bankAccountInfo.accountType as 'checking' | 'savings',
            //         },
            //         billing_details: {
            //             name: bankAccountInfo.accountHolderName,
            //             email,
            //         },
            //     });

            //     if (result.error) {
            //         message.error('Bank payment creation failed');
            //     } else {
            //         const paymentMethodId = result.paymentMethod?.id;
            //         const res2 = await channelPay({
            //             channelId: channel?.id!,
            //             paymentMethodId,
            //             email,
            //             country: bankAccountInfo.country,
            //             postalCode: bankAccountInfo.zipCode,
            //         });
            //         if (!res2.error) {
            //             navigate(`${PATH.CHANNEL_PAYMENT_SUCCESS}`);
            //         }
            //     }
            //     setLoading(false);
            // } catch (error) {
            //     console.error('Error creating bank PaymentMethod:', error);
            // }
        }
    };

    return (
        <div className={s.wrap}>
            <div className={s.container}>
                {/* 左侧内容 */}
                <div className={s.leftSection}>
                    <div className={s.leftWrap}>
                        <div className={s.header}>
                            <div className={s.logo}>
                                <img src={channel?.logo} alt="Channel Logo" />
                            </div>
                            <div className={s.companyName}>Klarity Health</div>
                        </div>

                        <div className={s.content}>
                            <h1 className={s.title}>Join Klarity Health</h1>
                            <p className={s.subtitle}>
                                There are no subscription or monthly fees — you&rsquo;re only billed when a new patient books with you.
                            </p>

                            <div className={s.featureList}>
                                <div className={s.featureItem}>
                                    <div className={s.checkIcon}>✓</div>
                                    <div className={s.featureText}>
                                        You&rsquo;ll be charged $80 lead generation fee 24 hours after a new patient books.
                                        If the patient cancels within that 24 hours window, you are not charged.
                                    </div>
                                </div>

                                <div className={s.featureItem}>
                                    <div className={s.checkIcon}>✓</div>
                                    <div className={s.featureText}>
                                        There are no charges for follow-up visits. You&rsquo;re only billed once per new patient lead.
                                    </div>
                                </div>

                                <div className={s.featureItem}>
                                    <div className={s.checkIcon}>✓</div>
                                    <div className={s.featureText}>
                                        You can set your own no-show/cancellation policy and decide to charge patients
                                        accordingly when those terms are not followed.
                                    </div>
                                </div>

                                <div className={s.featureItem}>
                                    <div className={s.checkIcon}>✓</div>
                                    <div className={s.featureText}>
                                        Klarity collects the patient&rsquo;s contact information and insurance details upon
                                        booking to help you verify eligibility in advance.
                                    </div>
                                </div>

                                <div className={s.featureItem}>
                                    <div className={s.checkIcon}>✓</div>
                                    <div className={s.featureText}>
                                        We encourage patients to securely add a credit card on file via the patient
                                        portal to support enforcement of your cancellation policy.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 右侧支付表单 */}
                <div className={s.rightSection}>
                    <div className={s.rightWrap}>
                        <div className={s.paymentCard}>
                            <div className={s.paymentHeader}>
                                <h2 className={s.paymentTitle}>Payment detail</h2>
                                <p className={s.paymentSubtitle}>
                                    Input your payment card details below. You&rsquo;ll only be charged when a new patient books an appointment.
                                </p>
                            </div>

                            <div className={s.formSection}>
                                {/* Email 输入框 */}
                                <div className={s.formGroup}>
                                    <label className={s.label}>Email</label>
                                    <Input
                                        size="large"
                                        className={s.input}
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            handleInputChange(e.target.value, filedName.email);
                                        }}
                                        placeholder=""
                                    />
                                    {
                                        showErrors && fieldsVerifyResult[filedName.email].error && <div className={s.error}>{fieldsVerifyResult[filedName.email].message}</div>
                                    }
                                </div>

                                {/* 支付方式选择 */}
                                <div className={s.formGroup}>
                                    <label className={s.label}>Payment method</label>
                                    <div className={s.paymentMethods}>
                                        <div
                                            className={`${s.paymentOption} ${selectedPaymentMethod === 'bank' ? s.selected : ''}`}
                                            onClick={() => setSelectedPaymentMethod('bank')}
                                        >
                                            <div className={s.radioButton}>
                                                {selectedPaymentMethod === 'bank' && <div className={s.radioSelected} />}
                                            </div>
                                            <div className={s.paymentIcon}>
                                                <div className={s.bankIcon}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M5.00031 7.5V14H6.50031V7.5H9.50031V14H11.0003V7.5H14.0003V14H15.0003C15.2655 14 15.5199 14.1054 15.7074 14.2929C15.895 14.4804 16.0003 14.7348 16.0003 15V16H0.000313816V15C0.000313816 14.7348 0.105671 14.4804 0.293207 14.2929C0.480743 14.1054 0.735097 14 1.00031 14H2.00031V7.5H5.00031ZM8.00031 0C12.6813 2.572 15.1813 3.95 15.5003 4.134C15.6925 4.24488 15.8424 4.41655 15.9263 4.62197C16.0103 4.82739 16.0235 5.05489 15.9639 5.26865C15.9044 5.48241 15.7754 5.67028 15.5973 5.80269C15.4192 5.93509 15.2022 6.00451 14.9803 6H1.02031C0.798455 6.00451 0.581399 5.93509 0.403324 5.80269C0.22525 5.67028 0.0962732 5.48241 0.0367046 5.26865C-0.0228641 5.05489 -0.00964091 4.82739 0.0742918 4.62197C0.158224 4.41655 0.308099 4.24488 0.500314 4.134C0.820314 3.95 3.32031 2.572 8.00031 0Z" fill="#15191E" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <span className={s.paymentLabel}>Bank</span>
                                        </div>
                                        {
                                            selectedPaymentMethod === 'bank' && (
                                                <div className={s.bankWrap}>
                                                    <div className={s.bankDisclaimer}>
                                                        <div className={s.disclaimerIcon}>⚠️</div>
                                                        <div className={s.disclaimerText}>
                                                            Bank accounts require verification via microdeposits, which may take 1-2 business days.
                                                        </div>
                                                    </div>
                                                    <div className={s.mandateText}>
                                                        By saving your bank account and clicking the confirm button, you authorize Klarity to electronically debit the account you provided for any amount owed for charges arising from your use of Klarity&apos;s services and/or purchase of products from Klarity, pursuant to Klarity&apos;s website and terms, until this authorization is revoked. You may amend or cancel this authorization at any time by providing notice to Klarity with 30 (thirty) days notice.
                                                    </div>
                                                    <div className={s.formRow}>
                                                        <div className={s.formItem}>
                                                            <div className={s.label}>Account holder name</div>
                                                            <div className={s.inputBox}>
                                                                <Input
                                                                    value={bankAccountInfo.accountHolderName}
                                                                    onChange={(e) => handleBankInputChange('accountHolderName', e.target.value)}
                                                                    placeholder="Full name on account"
                                                                />
                                                            </div>
                                                            {
                                                                showErrors && !bankAccountInfo.accountHolderName && <div className={s.error}>Account holder name is required</div>
                                                            }
                                                        </div>
                                                    </div>
                                                    {
                                                        !bankUsePaymentIntant && (
                                                            <>
                                                                <div className={s.formRow}>
                                                                    <div className={s.formItem}>
                                                                        <div className={s.label}>Routing number</div>
                                                                        <div className={s.inputBox}>
                                                                            <Input
                                                                                value={bankAccountInfo.routingNumber}
                                                                                onChange={(e) => handleBankInputChange('routingNumber', e.target.value)}
                                                                                placeholder="9-digit routing number"
                                                                                maxLength={9}
                                                                            />
                                                                        </div>
                                                                        {
                                                                            showErrors && !bankAccountInfo.routingNumber && <div className={s.error}>Routing number is required</div>
                                                                        }
                                                                    </div>
                                                                    <div className={s.formItem}>
                                                                        <div className={s.label}>Account number</div>
                                                                        <div className={s.inputBox}>
                                                                            <Input
                                                                                value={bankAccountInfo.accountNumber}
                                                                                onChange={(e) => handleBankInputChange('accountNumber', e.target.value)}
                                                                                placeholder="Account number"
                                                                                maxLength={17}
                                                                            />
                                                                        </div>
                                                                        {
                                                                            showErrors && !bankAccountInfo.accountNumber && <div className={s.error}>Account number is required</div>
                                                                        }
                                                                    </div>
                                                                </div>

                                                                <div className={s.formRow}>
                                                                    <div className={s.formItem}>
                                                                        <div className={s.label}>Account type</div>
                                                                        <div className={s.inputBox}>
                                                                            <Select
                                                                                value={bankAccountInfo.accountType}
                                                                                onChange={(value) => handleBankInputChange('accountType', value)}
                                                                                options={[
                                                                                    { label: 'Checking', value: 'checking' },
                                                                                    { label: 'Savings', value: 'savings' },
                                                                                ]}
                                                                                placeholder="Select account type"
                                                                                className={s.select}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )
                                                    }
                                                    <div className={s.formRow}>
                                                        <div className={s.formItem}>
                                                            <div className={s.label}>Country</div>
                                                            <div className={s.inputBox}>
                                                                <Select
                                                                    showSearch
                                                                    filterOption={(inputValue, option) =>
                                                                        option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                                                                    }
                                                                    value={bankAccountInfo.country}
                                                                    options={countries}
                                                                    className={s.select}
                                                                    onChange={(e) => {
                                                                        handleBankInputChange('country', e);
                                                                    }}
                                                                />
                                                            </div>
                                                            {
                                                                showErrors && !bankAccountInfo.country && <div className={s.error}>Country is required</div>
                                                            }
                                                        </div>
                                                        <div className={s.formItem}>
                                                            <div className={s.label}>Postal code</div>
                                                            <div className={s.inputBox}>
                                                                <Input
                                                                    value={bankAccountInfo.zipCode}
                                                                    type="number"
                                                                    maxLength={8}
                                                                    onChange={(e) => {
                                                                        handleBankInputChange('zipCode', e.target.value);
                                                                    }}
                                                                />
                                                            </div>
                                                            {
                                                                showErrors && !bankAccountInfo.zipCode && <div className={s.error}>Postal code is required</div>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }

                                        <div
                                            className={`${s.paymentOption} ${selectedPaymentMethod === 'card' ? s.selected : ''}`}
                                            onClick={() => setSelectedPaymentMethod('card')}
                                        >
                                            <div className={s.radioButton}>
                                                {selectedPaymentMethod === 'card' && <div className={s.radioSelected} />}
                                            </div>
                                            <div className={s.paymentIcon}>
                                                <div className={s.cardIcon}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12" fill="none">
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M0 2C0 1.46957 0.210714 0.960859 0.585786 0.585786C0.960859 0.210714 1.46957 0 2 0H14C14.5304 0 15.0391 0.210714 15.4142 0.585786C15.7893 0.960859 16 1.46957 16 2H0ZM0 4V10C0 10.5304 0.210714 11.0391 0.585786 11.4142C0.960859 11.7893 1.46957 12 2 12H14C14.5304 12 15.0391 11.7893 15.4142 11.4142C15.7893 11.0391 16 10.5304 16 10V4H0ZM3 9C3 8.73478 3.10536 8.48043 3.29289 8.29289C3.48043 8.10536 3.73478 8 4 8H5C5.26522 8 5.51957 8.10536 5.70711 8.29289C5.89464 8.48043 6 8.73478 6 9C6 9.26522 5.89464 9.51957 5.70711 9.70711C5.51957 9.89464 5.26522 10 5 10H4C3.73478 10 3.48043 9.89464 3.29289 9.70711C3.10536 9.51957 3 9.26522 3 9Z" fill="#15191E" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <span className={s.paymentLabel}>Card</span>
                                            <div className={s.cardBrands}>
                                                {
                                                    selectedPaymentMethod !== 'card' && (
                                                        <div className={s.cardIconList}>
                                                            <div className={`${s.card} ${s.mastercard}`} />
                                                            <div className={`${s.card} ${s.amex}`} />
                                                            <div className={`${s.card} ${s.discover}`} />
                                                            <div className={`${s.card} ${s.visa}`} />
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </div>
                                        {
                                            selectedPaymentMethod === 'card' && (
                                                <div className={s.cardWrap}>
                                                    <div className={s.cardTips}>A 3% processing fee applies when paying by credit card.</div>
                                                    <div className={s.formRow}>
                                                        <div className={s.formItem}>
                                                            <div className={s.label}>Card Number</div>
                                                            <div className={s.inputBox}>
                                                                <div className={s.inputBoxInner}>
                                                                    <CardNumberElement className={`${s.input} ${s.inputWithoutBorder}`} onChange={(e) => handleInputChange(e, filedName.cardNumber)} />
                                                                    <div className={s.cardIconList}>
                                                                        <div className={`${s.card} ${s.mastercard}`} />
                                                                        <div className={`${s.card} ${s.amex}`} />
                                                                        <div className={`${s.card} ${s.discover}`} />
                                                                        <div className={`${s.card} ${s.visa}`} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {
                                                                showErrors && fieldsVerifyResult[filedName.cardNumber].error && <div className={s.error}>{fieldsVerifyResult[filedName.cardNumber].message}</div>
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className={s.formRow}>
                                                        <div className={s.formItem}>
                                                            <div className={s.label}>Expiration</div>
                                                            <div className={s.inputBox}>
                                                                <CardExpiryElement className={s.input} onChange={(e) => handleInputChange(e, filedName.cardExpiry)} />
                                                            </div>
                                                            {
                                                                showErrors && fieldsVerifyResult[filedName.cardExpiry].error && <div className={s.error}>{fieldsVerifyResult[filedName.cardExpiry].message}</div>
                                                            }
                                                        </div>
                                                        <div className={s.formItem}>
                                                            <div className={s.label}>CVC</div>
                                                            <div className={s.inputBox}>
                                                                <CardCvcElement className={s.input} onChange={(e) => handleInputChange(e, filedName.cardCvc)} />
                                                            </div>
                                                            {
                                                                showErrors && fieldsVerifyResult[filedName.cardCvc].error && <div className={s.error}>{fieldsVerifyResult[filedName.cardCvc].message}</div>
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className={s.formRow}>
                                                        <div className={s.formItem}>
                                                            <div className={s.label}>Country</div>
                                                            <div className={s.inputBox}>
                                                                <Select
                                                                    showSearch
                                                                    filterOption={(inputValue, option) =>
                                                                        option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                                                                    }
                                                                    value={country}
                                                                    options={countries}
                                                                    className={s.select}
                                                                    onChange={(e) => handleInputChange(e, filedName.country)}
                                                                />
                                                            </div>
                                                            {
                                                                showErrors && fieldsVerifyResult[filedName.country].error && <div className={s.error}>{fieldsVerifyResult[filedName.country].message}</div>
                                                            }
                                                        </div>
                                                        <div className={s.formItem}>
                                                            <div className={s.label}>Postal code</div>
                                                            <div className={s.inputBox}>
                                                                <Input value={zipCode} type="number" onChange={(e) => handleInputChange(e.target.value, filedName.zipCode)} />
                                                            </div>
                                                            {
                                                                showErrors && fieldsVerifyResult[filedName.zipCode].error && <div className={s.error}>{fieldsVerifyResult[filedName.zipCode].message}</div>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }

                                        {/* <div
                                            className={`${s.paymentOption} ${selectedPaymentMethod === 'applepay' ? s.selected : ''}`}
                                            onClick={() => setSelectedPaymentMethod('applepay')}
                                        >
                                            <div className={s.radioButton}>
                                                {selectedPaymentMethod === 'applepay' && <div className={s.radioSelected} />}
                                            </div>
                                            <div className={s.paymentIcon}>
                                                <div className={s.appleIcon}>
                                                    <img src={ApplePay} alt="Apple Pay" />
                                                </div>
                                            </div>
                                            <span className={s.paymentLabel}>Apple Pay</span>
                                        </div>

                                        <div
                                            className={`${s.paymentOption} ${selectedPaymentMethod === 'cashapp' ? s.selected : ''}`}
                                            onClick={() => setSelectedPaymentMethod('cashapp')}
                                        >
                                            <div className={s.radioButton}>
                                                {selectedPaymentMethod === 'cashapp' && <div className={s.radioSelected} />}
                                            </div>
                                            <div className={s.paymentIcon}>
                                                <div className={s.cashAppIcon}>
                                                    <img src={CashApp} alt="Cash Pay" />
                                                </div>
                                            </div>
                                            <span className={s.paymentLabel}>Cash App Pay</span>
                                        </div>

                                        <div
                                            className={`${s.paymentOption} ${s.lastOption} ${selectedPaymentMethod === 'googlepay' ? s.selected : ''}`}
                                            onClick={() => setSelectedPaymentMethod('googlepay')}
                                        >
                                            <div className={s.radioButton}>
                                                {selectedPaymentMethod === 'googlepay' && <div className={s.radioSelected} />}
                                            </div>
                                            <div className={s.paymentIcon}>
                                                <div className={s.googleIcon}>
                                                    <img src={GooglePay} alt="Google Pay" />
                                                </div>
                                            </div>
                                            <span className={s.paymentLabel}>Google Pay</span>
                                        </div> */}
                                    </div>
                                </div>

                                {/* 确认按钮 */}
                                <div className={s.buttonSection}>
                                    <Button
                                        className={s.confirmButton}
                                        onClick={handleConfirm}
                                        size="large"
                                        type="primary"
                                        loading={loading}
                                    >
                                        Confirm
                                    </Button>

                                    <div className={s.securityInfo}>
                                        <div className={s.securityLeft}>
                                            <div className={s.shieldIcon}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <path d="M10.0026 2.49814L10.3309 1.73147C10.2272 1.687 10.1155 1.66406 10.0026 1.66406C9.88972 1.66406 9.77802 1.687 9.67427 1.73147L10.0026 2.49814ZM10.0026 17.4981L9.58927 18.2215C9.71516 18.2934 9.85763 18.3312 10.0026 18.3312C10.1476 18.3312 10.2901 18.2934 10.4159 18.2215L10.0026 17.4981ZM9.67427 1.7323L4.34594 4.0148L5.0026 5.54814L10.3309 3.2648L9.67427 1.7323ZM3.33594 5.5473V11.264H5.0026V5.5473H3.33594ZM6.2751 16.3281L9.58927 18.2215L10.4159 16.7748L7.10177 14.8806L6.2751 16.3281ZM10.4159 18.2215L13.7301 16.3281L12.9034 14.8806L9.58927 16.7748L10.4159 18.2215ZM16.6693 11.2631V5.54814H15.0026V11.2648L16.6693 11.2631ZM15.6593 4.01564L10.3309 1.7323L9.67427 3.26397L15.0026 5.54814L15.6593 4.01564ZM16.6693 5.54814C16.6692 5.22202 16.5735 4.90308 16.394 4.63082C16.2145 4.35857 15.959 4.14413 15.6593 4.01564L15.0026 5.54814H16.6693ZM13.7301 16.3281C14.6231 15.8178 15.3654 15.0813 15.8815 14.1917C16.3977 13.302 16.6695 12.2917 16.6693 11.2631H15.0026C15.0025 11.9977 14.8082 12.7192 14.4394 13.3545C14.0706 13.9898 13.5405 14.5163 12.9026 14.8806L13.7301 16.3281ZM3.33594 11.2631C3.33589 12.2915 3.60773 13.3017 4.12389 14.1912C4.64005 15.0807 5.3822 15.8179 6.2751 16.3281L7.10177 14.8806C6.46432 14.5163 5.93446 13.99 5.56583 13.3551C5.1972 12.7201 5.0029 11.999 5.0026 11.2648L3.33594 11.2631ZM4.34594 4.0148C4.04607 4.14335 3.79052 4.35792 3.61098 4.63034C3.43144 4.90275 3.33581 5.22188 3.33594 5.54814H5.0026L4.34594 4.0148Z" fill="#34D399" />
                                                    <path d="M12.5 8.32812L9.16667 11.6615L7.5 9.99479" stroke="#34D399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                            <span className={s.securityText}>Guaranteed safe & secure checkout</span>
                                        </div>
                                        <div className={s.stripeInfo}>
                                            <span className={s.poweredBy}>powered by</span>
                                            <span className={s.stripeLogo}>stripe</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
